import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { X, Camera, Save } from "lucide-react";
import { resizeImage } from "../utils/resizeImage";
import { cropImageFile } from "../utils/cropImage";
import { validateImageFile } from "../utils/imageValidation";
import VideoUploader from "../ui/VideoUploader"; // Adjust the import based on your file structure
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { addDoc, collection, doc, getDocs, serverTimestamp, updateDoc } from "firebase/firestore";

type ContentType = "punishment" | "draft_idea";

const Configure = () => {
  const [contentType, setContentType] = useState<ContentType>("punishment");

  // Card Image State
  const [cardImagePreview, setCardImagePreview] = useState<string | null>(null);
  const [selectedCardImage, setSelectedCardImage] = useState<File | null>(null);
  const cardFileInputRef = useRef<HTMLInputElement>(null);

  // Recipe Image State
  const [recipeImagePreview, setRecipeImagePreview] = useState<string | null>(null);
  const [selectedRecipeImage, setSelectedRecipeImage] = useState<File | null>(null);
  const recipeFileInputRef = useRef<HTMLInputElement>(null);

  // Video State
  const [cardVideoUrl, setCardVideoUrl] = useState<string | null>(null);
  const [recipeVideoUrl, setRecipeVideoUrl] = useState<string | null>(null);
  const [cardVideoFile, setCardVideoFile] = useState<File | null>(null);
  const [recipeVideoFile, setRecipeVideoFile] = useState<File | null>(null);

  // Cropper State
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImageType, setCropperImageType] = useState<"card" | "recipe" | null>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 9, y: 16 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

    const [draftIdeaDefaultImageId, setDraftIdeaDefaultImageId] = useState<string | null>(null);
    const [punishmenDefaultImagetId, setPunishmentDefaultImageId] = useState<string | null>(null);
    const [collectionName, setCollectionName] = useState<string>(contentType === "punishment" ? "default_punishments_images" : "default_draft_ideas_images");
    
  // Handle Content Type Change
  const handleContentTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setContentType(e.target.value as ContentType);
    setDraftIdeaDefaultImageId(null);
    setPunishmentDefaultImageId(null);
    setCardVideoUrl(null);
    setRecipeVideoUrl(null);
    setCardVideoFile(null);
    setRecipeVideoFile(null);
    setCollectionName(e.target.value === "punishment" ? "default_punishments_images" : "default_draft_ideas_images");
    if (recipeFileInputRef.current) recipeFileInputRef.current.value = "";
    //setCardImagePreview(null); setSelectedCardImage(null);
    //setRecipeImagePreview(null); setSelectedRecipeImage(null);
  };

  useEffect(() => {
    // Fetch existing default images from Firestore
    const fetchDefaultImages = async () => {
      try {
        const collectionRef = collection(db, collectionName);
        const querySnapshot = await getDocs(collectionRef);
        querySnapshot.forEach((doc) => {
          if (doc.data().contentType === "punishment") {
            console.log(doc.data());
            setPunishmentDefaultImageId(doc.id);
          } else if (doc.data().contentType === "draft_idea") {
              console.log(doc.id)
            setDraftIdeaDefaultImageId(doc.id);
              console.log(doc.data());
          }
            setCardImagePreview(doc.data().cardImageUrl || null);
            setRecipeImagePreview(doc.data().recipeImageUrl || null);
            setCardVideoUrl(doc.data().cardVideoUrl || null);
            setRecipeVideoUrl(doc.data().recipeVideoUrl || null);
        });
      } catch (error) {
        console.error("Error fetching default images:", error);
      }
    };

    fetchDefaultImages();
  }, [collectionName]);

  // Handle Image Change (Card or Recipe)
  const handleImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    imageType: "card" | "recipe"
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validationError = validateImageFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoadingCropper(true);
    try {
      const { resizedUrl } = await resizeImage(file);
      setTempImageUrl(resizedUrl);
      setCropperImageType(imageType);
      setShowCropper(true);
      setError(null);
    } finally {
      setLoadingCropper(false);
    }
  };

  // Cropper Complete
  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  // Confirm Crop
  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea || !cropperImageType) return;
    const cropped = await cropImageFile(tempImageUrl, croppedArea);
    if (cropped) {
      if (cropperImageType === "card") {
        setSelectedCardImage(cropped);
        setCardImagePreview(URL.createObjectURL(cropped));
      } else {
        setSelectedRecipeImage(cropped);
        setRecipeImagePreview(URL.createObjectURL(cropped));
      }
    }
    setShowCropper(false);
    setTempImageUrl(null);
    setCropperImageType(null);
  }, [tempImageUrl, croppedArea, cropperImageType]);

  // Remove Image
  const handleRemoveImage = (imageType: "card" | "recipe") => {
    if (imageType === "card") {
      setSelectedCardImage(null);
      setCardImagePreview(null);
      if (cardFileInputRef.current) cardFileInputRef.current.value = "";
    } else {
      setSelectedRecipeImage(null);
      setRecipeImagePreview(null);
      if (recipeFileInputRef.current) recipeFileInputRef.current.value = "";
    }
  };

  const uploadImageToStorage = async (file: File, folder: string) => {
    const timestamp = Date.now();
    const imageRef = ref(storage, `${folder}/${timestamp}_${file.name}`);
    await uploadBytes(imageRef, file);
    return await getDownloadURL(imageRef);
  }

  // Save handler
  const handleSave = useCallback(async () => {
    setIsSaving(true);
    
      console.log("Content Type:", contentType);
      console.log("Card Image File:", selectedCardImage);
      console.log("Recipe Image File:", selectedRecipeImage);
      console.log("Card Video URL:", cardVideoUrl);
      console.log("Recipe Video URL:", recipeVideoUrl);

      let payload: { contentType: ContentType; 
                        cardImageUrl?: string; 
                        recipeImageUrl?: string, 
                        cardVideoUrl?: string,  
                        recipeVideoUrl?: string
                    } = {
        contentType: contentType
      }

      if(selectedCardImage) {
        try {
            let cardImageUrl = await uploadImageToStorage(selectedCardImage, 'default_images')
            payload.cardImageUrl = cardImageUrl;
        } catch (error) {
            console.error("Error uploading card image:", error);
            setIsSaving(false);
            return; 
        }
      }

      if(selectedRecipeImage) {
        try {
            let recipeImageUrl = await uploadImageToStorage(selectedRecipeImage, 'default_images');
            payload.recipeImageUrl = recipeImageUrl;
        } catch (error) {
          console.error("Error uploading recipe image:", error);
          setIsSaving(false);
          return;   
            
        }
      }

      if (cardVideoUrl) {
          payload.cardVideoUrl = cardVideoUrl;
      }

      if (recipeVideoUrl){
        payload.recipeVideoUrl = recipeVideoUrl;
      }
        console.log("Payload to save:", payload);
      // add or update the payload in firestore collection "default_images"
        let doc = await saveIntoFireStore(payload);
        console.log("Document saved with ID:", doc);
      setIsSaving(false);
   
  }, [
    contentType,
    selectedCardImage,
    selectedRecipeImage,
    cardVideoUrl,
    recipeVideoUrl,
    cardVideoFile,
    recipeVideoFile,
  ]);

  const saveIntoFireStore = async (payload: any) => {
      if (punishmenDefaultImagetId) {
        // Update existing document
          return await updateDoc(doc(db, collectionName, punishmenDefaultImagetId), payload);
      }
      if (draftIdeaDefaultImageId){
        // Update existing document
          return await updateDoc(doc(db, collectionName, draftIdeaDefaultImageId), payload);
      }

      if(!punishmenDefaultImagetId && !draftIdeaDefaultImageId) {
        // Create new document
          return await addDoc(collection(db, collectionName), {
            ...payload,
            createdAt: serverTimestamp(),
        });
      }

  }

  return (
    <>
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-2xl font-bold mb-4">Default Images</h2>
          <div className="grid gap-4 md:grid-cols-1">
            <div>
              <label className="block text-sm font-medium mb-2">Content Type</label>
              <select
                className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={contentType}
                onChange={handleContentTypeChange}
              >
                <option value="punishment">Punishment</option>
                <option value="draft_idea">Draft Ideas</option>
              </select>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Card Image */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Card Image ({contentType === "punishment" ? "Punishment" : "Draft Idea"})
              </label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
                <div className="space-y-2 text-center">
                  <div className="flex flex-col items-center">
                    {cardImagePreview ? (
                      <div className="relative">
                        <img
                          src={cardImagePreview}
                          alt="Preview"
                          className="max-h-48 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage("card")}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <Camera size={48} className="mx-auto text-gray-400" />
                    )}
                  </div>
                  <label
                    htmlFor="card-image-upload"
                    className="relative cursor-pointer rounded-md font-medium text-[#18f7b1] hover:text-[#0fd494]"
                  >
                    {cardImagePreview ? "Change image" : "Upload an image"}
                    <input
                      id="card-image-upload"
                      ref={cardFileInputRef}
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={e => handleImageChange(e, "card")}
                    />
                  </label>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, JPEG, WEBP up to 10MB
                  </p>
                </div>
              </div>
            </div>
            {/* Recipe Image */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Recipe Image ({contentType === "punishment" ? "Punishment" : "Draft Idea"})
              </label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
                <div className="space-y-2 text-center">
                  <div className="flex flex-col items-center">
                    {recipeImagePreview ? (
                      <div className="relative">
                        <img
                          src={recipeImagePreview}
                          alt="Preview"
                          className="max-h-48 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveImage("recipe")}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <Camera size={48} className="mx-auto text-gray-400" />
                    )}
                  </div>
                  <label
                    htmlFor="recipe-image-upload"
                    className="relative cursor-pointer rounded-md font-medium text-[#18f7b1] hover:text-[#0fd494]"
                  >
                    {recipeImagePreview ? "Change image" : "Upload an image"}
                    <input
                      id="recipe-image-upload"
                      ref={recipeFileInputRef}
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={e => handleImageChange(e, "recipe")}
                    />
                  </label>
                  <p className="text-sm text-gray-500">
                    PNG, JPG, JPEG, WEBP up to 10MB
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            {/* Card Video */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Card Video ({contentType === "punishment" ? "Punishment" : "Draft Idea"})
              </label>
              <VideoUploader
                key={`card-video-${contentType}`}
                value={cardVideoUrl}
                onUploadSuccess={(url:string) => setCardVideoUrl(url)}
              />
            </div>

            {/* Recipe Video */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Recipe Video ({contentType === "punishment" ? "Punishment" : "Draft Idea"})
              </label>
              <VideoUploader
                key={`recipe-video-${contentType}`}
                value={recipeVideoUrl}
                onUploadSuccess={(url: string) => setRecipeVideoUrl(url)}
              />
            </div>
          </div>
        </div>

        {/* Cropper Modal */}
        {loadingCropper && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center">
            <div className="text-white text-xl flex items-center gap-4">
              <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-white border-opacity-70" />
              Preparing image for cropping...
            </div>
          </div>
        )}
        {showCropper && tempImageUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center">
            <div className="relative w-[90vw] max-w-2xl h-[90vh] bg-white rounded-lg overflow-hidden shadow-lg">
              <Cropper
                image={tempImageUrl}
                crop={crop}
                zoom={zoom}
                aspect={9 / 16}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
              <div className="absolute bottom-0 left-0 w-full flex justify-between p-4 bg-white border-t">
                <button
                  type="button"
                  onClick={() => setShowCropper(false)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCropConfirm}
                  className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                >
                  Crop & Save
                </button>
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
            <span className="text-red-700">{error}</span>
          </div>
        )}

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] flex items-center gap-2 transition-all ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black" />
                Saving...
              </>
            ) : (
              <>
                <Save size={20} />
                Save
              </>
            )}
          </button>
        </div>
      </div>
    </>
  );
};

export default Configure;